import React, { useEffect, useRef, useState } from "react";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import AllowedTo from "../../shared/common/rbac";
import {
  AddIcon,
  ApproveIcon,
  CancelIcon,
  CaretDownSolidIcon,
  CaretUpSolidIcon,
  EditIcon,
  FilterIcon,
  ResetIcon,
} from "../../../assets/icons";
import {
  ILessonPlan,
  ILessonPlanSearch,
  searchLessonPlans,
  getLessonPlanBySubjectId,
} from "../../../api/lessonPlans.service";
import { PagingResponse } from "../../../model/interface/pagingResponse";
import Pagination from "../../shared/common/pagination";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import constant from "../../../utils/constant";
import { Link, useNavigate } from "react-router";

const defaultFilter: ILessonPlanSearch = {
  orderByColumn: "SubjectId",
  sortDir: "ASC",
  pageNumber: 1,
  pageSize: 10,
};

export const LessonPlans = () => {
  const [subjectId, setSubjectId] = useState<number>();
  const [filter, setFilter] = useState<ILessonPlanSearch>(defaultFilter);
  const [lessonPlans, setLessonPlans] = useState<PagingResponse<ILessonPlan>>({
    data: [],
    totalRecords: 0,
  });

  const navigate = useNavigate();

  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);

  const handleSubjectIdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = parseInt(e.target.value);
    setSubjectId(id);
  };

  const handleSortBy = (column: string) => {
    if (filter.orderByColumn === column) {
      setFilter({
        ...filter,
        sortDir: filter.sortDir == "DESC" ? "ASC" : "DESC",
      });
    } else {
      setFilter({ ...filter, sortDir: "ASC", orderByColumn: column });
    }
  };

  const handleFilterClick = async () => {
    setFilter({
      ...filter,
      pageNumber: 1,
    });
    paginationRef?.current?.onReset();
  };

  const handleResetFilterClick = async () => {
    setSubjectId(undefined);
    setFilter({
      ...filter,
      pageNumber: 1,
    });
    paginationRef?.current?.onReset();
  };

  const handleAddLessonPlanClick = () => {
    navigate(constant.LESSONPLANS_NEW);
  };

  const handleEditclick = (lessonPlanId: number) => {
    navigate(
      constant.LESSONPLANS_EDIT.replace(
        ":lessonPlanId",
        lessonPlanId.toString()
      )
    );
  };

  const loadLessonPlans = async () => {
    let data: PagingResponse<ILessonPlan> = { data: [], totalRecords: 0 };
    if (subjectId != undefined) {
      const results = await getLessonPlanBySubjectId(subjectId);
      if (results.data == null) {
        data = {
          data: [],
          totalRecords: 0,
        };
      } else {
        data = {
          data: [results.data],
          totalRecords: 1,
        };
      }
    } else {
      const results = await searchLessonPlans(filter);
      data = results.data;
    }
    setLessonPlans(data);
  };

  useEffect(() => {
    loadLessonPlans();
  }, [filter]);

  return (
    <AllowedTo
      perform={Permissions.lesson_plan_view}
      no={() => <AccessDenied />}
    >
      <div className="flex-1">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Lesson plans</h3>
              <AllowedTo perform={Permissions.lesson_plan_create}>
                <button
                  onClick={() => handleAddLessonPlanClick()}
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transsition ease-in-out duration-300"
                >
                  <AddIcon className="w-5"></AddIcon>
                  Add Lesson Plan
                </button>
              </AllowedTo>
            </div>
            <div className="pt-6 border-b pb-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-2 sm:col-span-2  ">
                  <label className="text-sm text-gray-700">Subject Id</label>
                  <div className="contentArea mt-1">
                    <input
                      id="subjectId"
                      name="subjectId"
                      value={subjectId || ""}
                      onChange={handleSubjectIdChanged}
                      type="text"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  onClick={handleFilterClick}
                >
                  <FilterIcon className="w-3 h-3"></FilterIcon>
                  <span>Filter</span>
                </button>
                <button
                  type="button"
                  onClick={handleResetFilterClick}
                  className="py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <ResetIcon className="w-3 h-3"></ResetIcon>
                  <span>Reset Filter</span>
                </button>
              </div>
            </div>
            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-5 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider cursor-pointer"
                              onClick={() => handleSortBy("SubjectId")}
                            >
                              <span className="flex">
                                SubjectId
                                {filter.orderByColumn === "SubjectId" && (
                                  <span>
                                    {filter.sortDir == "ASC" ? (
                                      <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                                    ) : (
                                      <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                                    )}
                                  </span>
                                )}
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Subject Name</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">Assets</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex justify-center items-center">
                                Approved
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex justify-center items-center">
                                Action
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {lessonPlans.data.map((lessonPlan, index) => (
                            <tr
                              key={lessonPlan.subjectId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                <Link
                                  to={constant.SUBJECTDETAIL.replace(
                                    ":id",
                                    lessonPlan.subjectId?.toString()
                                  )}
                                >
                                  {lessonPlan.subjectId}
                                </Link>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {lessonPlan.subjectName}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {lessonPlan.assetCount}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white flex justify-center items-center">
                                {lessonPlan.isApproved ? (
                                  <ApproveIcon
                                    className={"w-6 text-green-500"}
                                  />
                                ) : (
                                  <CancelIcon className={"w-6 text-red-700"} />
                                )}
                              </td>
                              <td>
                                <div className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white flex justify-center items-center">
                                  <AllowedTo
                                    perform={Permissions.lesson_plan_create}
                                  >
                                    <EditIcon
                                      onClick={() =>
                                        handleEditclick(lessonPlan.lessonPlanId)
                                      }
                                      className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                  </AllowedTo>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  {lessonPlans.data.length > 0 ? (
                    <>
                      <Pagination
                        pages={Math.ceil(
                          lessonPlans.totalRecords / filter.pageSize
                        )}
                        setPageNumber={(pageNumber) => {
                          if (filter.pageNumber !== pageNumber)
                            setFilter({ ...filter, pageNumber: pageNumber });
                        }}
                        ref={paginationRef}
                      />
                      <span>
                        <span className="text-sm pr-4 ml-auto text-left-700">
                          Showing {filter.pageNumber} to{" "}
                          {lessonPlans.totalRecords < filter.pageSize
                            ? lessonPlans.totalRecords
                            : filter.pageSize}{" "}
                          {"of "}
                          {lessonPlans.totalRecords} results
                        </span>

                        <SelectPageSize
                          setPageSize={(pageSize) => {
                            setFilter({ ...filter, pageSize: pageSize });
                          }}
                          type={"second"}
                          ref={pageSizeRef}
                        />
                      </span>
                    </>
                  ) : (
                    <p className="p-2">No Records Found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};
